import React, { useMemo } from 'react'
import { GridColDef } from '@mui/x-data-grid'
import { IconButton, Tooltip } from '@mui/material'
import { Message as MessageIcon } from '@mui/icons-material'
import { Message } from '../../../services/campaignApi'
import { formatCustomDateTime } from '../../../utils/dateUtils'
import { renderStatusChip } from './utils'
import { FilterableColumnHeader } from '../../common/table'

interface UseMessageColumnsProps {
  dynamicFilters: Record<string, string[]>
  apiRef: React.MutableRefObject<any>
  onOpenConversation: (msg: Message) => void
}

export const useMessageColumns = ({ dynamicFilters, apiRef, onOpenConversation }: UseMessageColumnsProps) => {
  const renderHeader = (field: string, label: string) => (
    <FilterableColumnHeader
      field={field}
      label={label}
      isFiltered={(dynamicFilters[field]?.length || 0) > 0}
      onToggleMenu={() => apiRef.current?.toggleColumnMenu(field)}
    />
  )

  return useMemo<GridColDef[]>(
    () => [
      {
        field: 'lead_name',
        headerName: 'Lead Name',
        minWidth: 150,
        width: 220,
        maxWidth: 300,
        sortable: true,
        renderHeader: () => renderHeader('lead_name', 'Lead Name'),
        valueGetter: (params) => params.row.lead_name,
      },
      {
        field: 'lead_phone',
        headerName: 'Phone',
        minWidth: 130,
        width: 180,
        maxWidth: 220,
        sortable: true,
        renderHeader: () => renderHeader('lead_phone', 'Phone'),
        valueGetter: (params) => params.row.lead_phone || '-',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 160,
        sortable: true,
        valueFormatter: (value: any) =>
          value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : '',
        renderHeader: () => renderHeader('status', 'Status'),
        renderCell: (params) => renderStatusChip(params.row),
      },
      {
        field: 'sent_at',
        headerName: 'Sent At',
        minWidth: 160,
        width: 200,
        maxWidth: 220,
        sortable: true,
        filterable: false,
        renderCell: (params) => formatCustomDateTime(params.row.sent_at, '-'),
      },
      {
        field: 'delivered_at',
        headerName: 'Delivered At',
        minWidth: 160,
        width: 200,
        maxWidth: 220,
        sortable: true,
        filterable: false,
        renderCell: (params) => formatCustomDateTime(params.row.delivered_at, '-'),
      },
      {
        field: 'read_at',
        headerName: 'Read At',
        minWidth: 160,
        width: 200,
        maxWidth: 220,
        sortable: true,
        filterable: false,
        renderCell: (params) => formatCustomDateTime(params.row.read_at, '-'),
      },
      {
        field: 'actions',
        headerName: 'Conversation',
        width: 140,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Tooltip title="View Conversation">
            <span>
              <IconButton
                size="small"
                onClick={() => onOpenConversation(params.row)}
                disabled={!params.row.conversation_id}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <MessageIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [dynamicFilters, onOpenConversation, apiRef]
  )
}
